import Button from "react-bootstrap/esm/Button";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import Col from "react-bootstrap/esm/Col";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import Form from "react-bootstrap/Form";
import ReportsTable from "../../components/tables/reportsTable";
import { ColumnDef } from "@tanstack/react-table";
import { fuzzySort } from "../../components/tables/fuzzySort";
import { CatchReportPeriod } from "../../models/catchReportPeriod";
import { getFormattedDateMMDDYYYY } from "../../helpers/getFormattedDateMMDDYYYY";
import { CatchReport } from "../../models/catchReport";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";
import { FaExternalLinkAlt, FaEye, FaFileDownload } from "react-icons/fa";
import { Link } from "react-router-dom";
import { getOrganizationID, getRoles } from "../../helpers/authHelper";
import Spinner from "react-bootstrap/Spinner";
import { Vessel } from "../../models/vessel";
import { Gear } from "../../models/gear";
import { Species } from "../../models/species";
import { lookupGear } from "../../api/lookupGear";
import { lookupTripTypes } from "../../api/lookupTripTypes";
import { lookupLakes } from "../../api/lookupLakes";
import { lookupGrids } from "../../api/lookupGrids";
import { lookupSpecies } from "../../api/lookupSpecies";
import { lookupMgmtUnitTypes } from "../../api/lookupMgmtUnitsTypes";
import { Lake } from "../../models/lake";
import { ManagementUnitType } from "../../models/managementUnitType";
import { lookupMgmtUnits, LookupMgmtUnits } from "../../api/lookupMgmtUnits";
import {
  PersonalCatchReport,
  getPersonalCatchReport,
} from "../../api/getPersonalCatchReport";
import Chart from "chart.js/auto";
import AnnualCatchReportChart from "../../components/charts/AnnualCatchReportChart";
import { downloadJSONAsCSV } from "../../helpers/downloadJSONAsCSV";
import { Grid } from "../../models/grid";

export default function PersonalCatch() {
  // data
  const [personalCatchReportData, setPersonalCatchReportData] = useState<any[]>([]);

  // State management
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // Filters
  const [beginDate, setBeginDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLakeID, setSelectedLakeID] = useState(-1);
  const [selectedGearID, setSelectedGearID] = useState(-1);
  const [selectedGridID, setSelectedGridID] = useState(-1);
  const [selectedSpeciesID, setSelectedSpeciesID] = useState(-1);
  const [selectedManagementUnitTypeID, setSelectedManagementUnitTypeID] =
    useState(-1);
  const [selectedManagementUnitID, setSelectedManagementUnitID] = useState(-1);

  // Lookups
  const [lakesList, setLakesList] = useState<Lake[]>([]);
  const [gearList, setGearList] = useState<Gear[]>([]);
  const [gridList, setGridList] = useState<Grid[]>([]);
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [managementUnitTypesList, setManagementUnitTypesList] = useState<any[]>(
    []
  );
  const [managementUnitsList, setManagementUnitsList] = useState<any[]>([]);

  // On load - get lookups
  useEffect(() => {
    lookupLakes().then((res) => {
      if (res) setLakesList(res);
    });
    lookupGrids().then((res) => {
      if (res) setGridList(res);
    });
    lookupGear().then((res) => {
      if (res) setGearList(res);
    });
    lookupSpecies().then((res) => {
      if (res) setSpeciesList(res);
    });
    lookupMgmtUnitTypes().then((res) => {
      if (res) setManagementUnitTypesList(res);
    });
  }, []);

  //Update values for management Unit
  useEffect(() => {
    const params: LookupMgmtUnits = {
      typeID: selectedManagementUnitTypeID,
    };
    lookupMgmtUnits(params).then((res) => {
      if (res) setManagementUnitsList(res);
    });
  }, [selectedManagementUnitTypeID]);

  // When filter review changes, fetch the new trips and update the table
  useEffect(() => {
    const params: PersonalCatchReport = {
      beginDate: beginDate,
      endDate: endDate,
      lakeID: selectedLakeID,
      gearID: selectedGearID,
      gridID: selectedGridID,
      speciesID: selectedSpeciesID,
      mgmtUnitID: selectedManagementUnitID,
    };

    getReport(params);
  }, [
    beginDate,
    endDate,
    selectedLakeID,
    selectedGearID,
    selectedGridID,
    selectedSpeciesID,
    selectedManagementUnitID,
  ]);

  const getReport = async (params: PersonalCatchReport) => {
    setIsLoadingReport(true);
    getPersonalCatchReport(params).then((res) => {
      setPersonalCatchReportData(res);
      setIsLoadingReport(false);
    });
  };

  const downloadReportAsCSV = async () => {
    if (personalCatchReportData.length === 0) return;
    setIsDownloadingCSV(true);
    downloadJSONAsCSV(personalCatchReportData, "Personal Catch Report.csv");
    setIsDownloadingCSV(false);
  };

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorFn: (row) => row["Year"],
        id: "year",
        header: "Year",
        cell: (info) => {
          return <strong>{info.getValue()}</strong>;
        },
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Converted Weight"],
        id: "Converted Weight",
        header: "Converted Weight",
        cell: (info) => info.getValue().toLocaleString('en-US') + " lbs",
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Reported Round"],
        id: "reportedRound",
        header: "Reported Round",
        cell: (info) => info.getValue().toLocaleString('en-US') + " lbs",
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Reported Fillet"],
        id: "reportedFillet",
        header: "Reported Fillet",
        cell: (info) => info.getValue().toLocaleString('en-US') + " lbs",
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Reported Dressed"],
        id: "reportedDressed",
        header: "Reported Dressed",
        cell: (info) => info.getValue().toLocaleString('en-US') + " lbs",
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Reported Discard"],
        id: "reportedDiscard",
        header: "Reported Discard",
        cell: (info) => info.getValue().toLocaleString('en-US') + " lbs",
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
      {
        accessorFn: (row) => row["Records"],
        id: "records",
        header: "Records",
        cell: (info) => info.getValue().toLocaleString('en-US'),
        footer: (props) => props.column.id,
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },
    ],
    [personalCatchReportData]
  );

  return (
    <div className="py-5">
      <Container fluid>
        <h1 className="text-primary mb-5 display-4 fw-bold">
          My Catch
        </h1>

        <div className="form-card">
          <Row className="gx-3">
            <Col xs="auto">
              <Form.Group className="">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={beginDate}
                  onChange={(e) => setBeginDate(e.currentTarget.value)}
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Group className="">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.currentTarget.value)}
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Form.Label>Lake</Form.Label>
              <Form.Select
                aria-label="Select Lake"
                value={selectedLakeID}
                onChange={(e) => {
                  setSelectedLakeID(parseInt(e.target.value));
                  setSelectedGridID(-1);
                }}
              >
                <option value={-1}>All Lakes</option>
                {Array.isArray(lakesList) &&
                  lakesList.map((lake: Lake) => (
                    <option key={lake.id} value={lake.id}>
                      {lake.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Grid</Form.Label>
              <Form.Select
                aria-label="Select Grid"
                value={selectedGridID}
                onChange={(e) => setSelectedGridID(parseInt(e.target.value))}
              >
                <option value={-1}>All Grids</option>
                {Array.isArray(gridList) &&
                  gridList
                    .filter((grid: Grid) =>
                      selectedLakeID > 0 ? grid.lakeID === selectedLakeID : true
                    )
                    .map((grid: Grid) => (
                      <option key={grid.id} value={grid.id}>
                        {grid.lakeCode}-{grid.gridNumber}
                      </option>
                    ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Gear</Form.Label>
              <Form.Select
                aria-label="Select Gear"
                value={selectedGearID}
                onChange={(e) => setSelectedGearID(parseInt(e.target.value))}
              >
                <option value={-1}>All Gears</option>
                {Array.isArray(gearList) &&
                  gearList.map((gear: Gear) => (
                    <option key={gear.id} value={gear.id}>
                      {gear.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Species</Form.Label>
              <Form.Select
                aria-label="Select Species"
                value={selectedSpeciesID}
                onChange={(e) => setSelectedSpeciesID(parseInt(e.target.value))}
              >
                <option value={-1}>All Species</option>
                {Array.isArray(speciesList) &&
                  speciesList.map((species: Species) => (
                    <option key={species.id} value={species.id}>
                      {species.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Management Unit Type</Form.Label>
              <Form.Select
                aria-label="Select Management Unit Type"
                value={selectedManagementUnitTypeID}
                onChange={(e) =>
                  setSelectedManagementUnitTypeID(parseInt(e.target.value))
                }
              >
                <option value={-1}>All Unit Types</option>
                {Array.isArray(managementUnitTypesList) &&
                  managementUnitTypesList.map(
                    (mgmtUnitType: ManagementUnitType) => (
                      <option key={mgmtUnitType.id} value={mgmtUnitType.id}>
                        {mgmtUnitType.name}
                      </option>
                    )
                  )}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Form.Label>Management Unit</Form.Label>
              <Form.Select
                aria-label="Select Management Unit"
                value={selectedManagementUnitID}
                onChange={(e) =>
                  setSelectedManagementUnitID(parseInt(e.target.value))
                }
              >
                <option value={-1}>All Units</option>
                {Array.isArray(managementUnitsList) &&
                  managementUnitsList.map((mgmtUnit: ManagementUnitType) => (
                    <option key={mgmtUnit.id} value={mgmtUnit.id}>
                      {mgmtUnit.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col xs="auto" className="pt-4">
              <Button variant="secondary" onClick={downloadReportAsCSV}>
                {isDownloadingCSV ? (
                  <Spinner
                    animation="border"
                    variant="light"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <FaFileDownload className="mt-n1 me-2" />
                )}
                Download Report as CSV
              </Button>
            </Col>
          </Row>
        </div>

        {isLoadingReport ? (
          <div className="d-flex justify-content-center align-items-center mb-5">
            <Spinner animation="border" variant="primary" className="ms-2" />
            <span className="h3 ms-3 mb-0 text-primary">
              Loading Personal Catch Data...
            </span>
          </div>
        ) : (
          <>
            <AnnualCatchReportChart
              annualCatchReportData={personalCatchReportData}
            />
            <ReportsTable
              className="mb-5"
              columns={columns}
              catchReports={personalCatchReportData}
            />
          </>
        )}
      </Container>
    </div>
  );
}
